import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  ExpandableSection,
  FormField,
  Header,
  Input,
  Pagination,
  Select,
  Slider,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  Table,
  Tabs,
} from '@cloudscape-design/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useMedia } from 'react-use';

import {
  getScorecardThresholds,
  updateScorecardThresholds,
} from '../../common/api/scorecardThresholds';
import { formatCustomerVolumeInput } from '../../common/customerVolume';
import { DashboardViewLayout } from '../../components/DashboardViewLayout';
import { ThresholdCalculator } from './ThresholdCalculator';
import {
  createThresholdsDraft,
  orderedGradeEntries,
  validateThresholdsDraft,
  type MetricDraft,
  type StatusDraft,
  type ThresholdDraft,
  type ThresholdsDraft,
} from './validation';

type DraftMutator = (draft: ThresholdsDraft) => void;
type DraftUpdater = (mutator: DraftMutator) => void;

function CustomerVolumeField({
  label,
  value,
  error,
  onChange,
}: {
  label: string;
  value: string;
  error?: string;
  onChange: (value: string) => void;
}) {
  return (
    <FormField label={label} constraintText="Default customers per month" errorText={error}>
      <Input
        type="text"
        inputMode="numeric"
        value={formatCustomerVolumeInput(value)}
        invalid={Boolean(error)}
        onChange={({ detail }) => onChange(formatCustomerVolumeInput(detail.value))}
      />
    </FormField>
  );
}

function SliderField({
  label,
  unit,
  value,
  min,
  max,
  step,
  error,
  onChange,
}: {
  label: string;
  unit: string;
  value: string;
  min: number;
  max: number;
  step: number;
  error?: string;
  onChange: (value: string) => void;
}) {
  const parsed = Number(value);
  const scale = 1 / step;
  const sliderMin = Math.round(Math.min(min, max) * scale);
  const sliderMax = Math.round(Math.max(min, max) * scale);
  const current = Number.isFinite(parsed)
    ? Math.min(sliderMax, Math.max(sliderMin, Math.round(parsed * scale)))
    : sliderMin;
  return (
    <FormField
      label={label}
      constraintText={`${value || '—'} ${unit} · Drag or enter an exact value`}
      errorText={error}
    >
      <div className="grid grid-cols-[minmax(0,1fr)_6rem] items-start gap-4">
        <Slider
          value={current}
          min={sliderMin}
          max={sliderMax}
          step={1}
          ariaLabel={label}
          valueFormatter={(next) => `${Number((next / scale).toFixed(6))} ${unit}`}
          onChange={({ detail }) => onChange(String(Number((detail.value / scale).toFixed(6))))}
        />
        <Input
          type="number"
          inputMode="decimal"
          ariaLabel={`${label} exact value`}
          value={value}
          invalid={Boolean(error)}
          onChange={({ detail }) => onChange(detail.value)}
        />
      </div>
    </FormField>
  );
}

function StatusFields({
  status,
  path,
  errors,
  update,
}: {
  status: StatusDraft;
  path: string;
  errors: Record<string, string>;
  update: DraftUpdater;
}) {
  const fields: Array<{ key: keyof StatusDraft; label: string }> = [
    { key: 'extremely_low', label: 'Extremely low ends' },
    { key: 'below', label: 'Below average ends' },
    { key: 'above', label: 'Above average starts' },
  ];
  return (
    <ColumnLayout columns={3} variant="text-grid">
      {fields.map(({ key, label }, index) => {
        const previous = index > 0 ? Number(status[fields[index - 1].key]) : -1;
        const next = index < fields.length - 1 ? Number(status[fields[index + 1].key]) : 101;
        return (
          <SliderField
            key={key}
            label={label}
            unit="%"
            value={status[key]}
            min={index === 0 ? 0 : Math.min(100, previous + 1)}
            max={index === fields.length - 1 ? 100 : Math.max(0, next - 1)}
            step={1}
            error={errors[`${path}.${key}`]}
            onChange={(value) =>
              update((draft) => {
                const target = path.split('.').reduce<unknown>((current, part) => {
                  return (current as Record<string, unknown>)[part];
                }, draft) as StatusDraft;
                target[key] = value;
              })
            }
          />
        );
      })}
    </ColumnLayout>
  );
}

function rebalanceWeights(
  weights: ThresholdsDraft['weights'],
  changedKey: string,
  value: string,
): void {
  weights[changedKey] = value;
  const parsed = Number(value);
  if (value.trim() === '' || !Number.isFinite(parsed) || parsed < 0 || parsed > 100) return;

  const otherKeys = Object.keys(weights).filter((key) => key !== changedKey);
  const remaining = 10_000 - Math.round(parsed * 100);
  const prior = otherKeys.map((key) => Math.max(0, Math.round(Number(weights[key]) * 100)));
  const priorTotal = prior.reduce((total, next) => total + next, 0);
  let assigned = 0;
  otherKeys.forEach((key, index) => {
    const cents =
      index === otherKeys.length - 1
        ? remaining - assigned
        : Math.round(
            priorTotal > 0 ? (remaining * prior[index]) / priorTotal : remaining / otherKeys.length,
          );
    weights[key] = String(Number((cents / 100).toFixed(2)));
    assigned += cents;
  });
}

function CurveTable({
  items,
  path,
  thresholdUnit,
  errors,
  updateItem,
  addItem,
  removeItem,
  direction,
  thresholdMax,
  thresholdStep,
}: {
  items: ThresholdDraft[];
  path: string;
  thresholdUnit: string;
  errors: Record<string, string>;
  updateItem: (index: number, key: keyof ThresholdDraft, value: string) => void;
  addItem: (index: number, item: ThresholdDraft) => void;
  removeItem: (index: number) => void;
  direction: 'ascending' | 'descending';
  thresholdMax: number;
  thresholdStep: number;
}) {
  const compact = useMedia('(max-width: 767px)');
  const pageSize = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [expanded, setExpanded] = useState(items.length <= pageSize);
  const pagesCount = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(currentPage, pagesCount);
  const startIndex = (safePage - 1) * pageSize;
  const pageItems = items.slice(startIndex, startIndex + pageSize).map((item, index) => ({
    ...item,
    index: startIndex + index,
  }));
  const thresholdBounds = (): { min: number; max: number } => ({ min: 0, max: thresholdMax });
  const allocationBounds = (index: number): { min: number; max: number } => ({
    min: Number(items[index + 1]?.allocation ?? 0),
    max: Number(items[index - 1]?.allocation ?? 100),
  });
  const addBand = () => {
    const last = items.at(-1);
    const insertIndex =
      last?.threshold === '0' && last.allocation === '0' ? items.length - 1 : items.length;
    const previous = items[insertIndex - 1];
    const next = items[insertIndex];
    const previousThreshold = Number(previous?.threshold ?? 0);
    const nextThreshold = Number(next?.threshold ?? 0);
    const threshold =
      direction === 'ascending'
        ? Math.min(thresholdMax, previousThreshold + Math.max(24, thresholdStep))
        : next
          ? (previousThreshold + nextThreshold) / 2
          : Math.max(0, previousThreshold - thresholdStep);
    const previousAllocation = Number(previous?.allocation ?? 100);
    const nextAllocation = Number(next?.allocation ?? 0);
    const snappedThreshold = Math.round(threshold / thresholdStep) * thresholdStep;
    addItem(insertIndex, {
      threshold: String(Number(snappedThreshold.toFixed(6))),
      allocation: String(Math.round((previousAllocation + nextAllocation) / 2)),
    });
    setCurrentPage(Math.floor(insertIndex / pageSize) + 1);
    setExpanded(true);
  };
  return (
    <ExpandableSection
      expanded={expanded}
      onChange={({ detail }) => setExpanded(detail.expanded)}
      headerText={`Allocation curve · ${items.length} ${items.length === 1 ? 'band' : 'bands'}`}
      headerActions={
        <Button iconName="add-plus" onClick={addBand}>
          Add band
        </Button>
      }
    >
      <Table
        variant="embedded"
        wrapLines
        items={pageItems}
        trackBy="index"
        pagination={
          pagesCount > 1 ? (
            <Pagination
              currentPageIndex={safePage}
              pagesCount={pagesCount}
              onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
            />
          ) : undefined
        }
        columnDefinitions={
          compact
            ? [
                {
                  id: 'threshold-mobile',
                  header: 'Bands',
                  cell: (item) => (
                    <SpaceBetween size="s">
                      <SliderField
                        label={`Threshold ${item.index + 1}`}
                        unit={thresholdUnit}
                        value={item.threshold}
                        {...thresholdBounds()}
                        step={thresholdStep}
                        error={errors[`${path}.${item.index}.threshold`]}
                        onChange={(value) => updateItem(item.index, 'threshold', value)}
                      />
                      <SliderField
                        label="Score allocation"
                        unit="%"
                        value={item.allocation}
                        {...allocationBounds(item.index)}
                        step={1}
                        error={errors[`${path}.${item.index}.allocation`]}
                        onChange={(value) => updateItem(item.index, 'allocation', value)}
                      />
                      <Button
                        iconName="remove"
                        disabled={items.length === 1}
                        onClick={() => removeItem(item.index)}
                      >
                        Remove band
                      </Button>
                    </SpaceBetween>
                  ),
                },
              ]
            : [
                {
                  id: 'band',
                  header: 'Band',
                  cell: (item) => <Box fontWeight="bold">{item.index + 1}</Box>,
                  width: 70,
                },
                {
                  id: 'threshold',
                  header: `Threshold (${thresholdUnit})`,
                  cell: (item) => (
                    <SliderField
                      label={`Band ${item.index + 1} threshold`}
                      unit={thresholdUnit}
                      value={item.threshold}
                      {...thresholdBounds()}
                      step={thresholdStep}
                      error={errors[`${path}.${item.index}.threshold`]}
                      onChange={(value) => updateItem(item.index, 'threshold', value)}
                    />
                  ),
                },
                {
                  id: 'allocation',
                  header: 'Score allocation (%)',
                  cell: (item) => (
                    <SliderField
                      label={`Band ${item.index + 1} allocation`}
                      unit="%"
                      value={item.allocation}
                      {...allocationBounds(item.index)}
                      step={1}
                      error={errors[`${path}.${item.index}.allocation`]}
                      onChange={(value) => updateItem(item.index, 'allocation', value)}
                    />
                  ),
                },
                {
                  id: 'actions',
                  header: 'Actions',
                  width: 120,
                  cell: (item) => (
                    <Button
                      variant="inline-icon"
                      iconName="remove"
                      ariaLabel={`Remove band ${item.index + 1}`}
                      disabled={items.length === 1}
                      onClick={() => removeItem(item.index)}
                    />
                  ),
                },
              ]
        }
      />
    </ExpandableSection>
  );
}

const METRICS: Array<{
  key: 'average_rating' | 'response_rate' | 'response_time';
  label: string;
  thresholdUnit: string;
  thresholdMax: number;
  thresholdStep: number;
  direction: 'ascending' | 'descending';
  criticalUnit: string;
  criticalMin: number;
  criticalMax: number;
  criticalStep: number;
}> = [
  {
    key: 'average_rating',
    label: 'Average rating',
    thresholdUnit: 'stars',
    thresholdMax: 5,
    thresholdStep: 0.1,
    direction: 'descending',
    criticalUnit: 'Stars',
    criticalMin: 0,
    criticalMax: 5,
    criticalStep: 0.1,
  },
  {
    key: 'response_rate',
    label: 'Response rate',
    thresholdUnit: '%',
    thresholdMax: 100,
    thresholdStep: 0.1,
    direction: 'descending',
    criticalUnit: 'Allocation percent (-1 disables)',
    criticalMin: -1,
    criticalMax: 100,
    criticalStep: 1,
  },
  {
    key: 'response_time',
    label: 'Response time',
    thresholdUnit: 'hours',
    thresholdMax: 720,
    thresholdStep: 1,
    direction: 'ascending',
    criticalUnit: 'Allocation percent (-1 disables)',
    criticalMin: -1,
    criticalMax: 100,
    criticalStep: 1,
  },
];

function MetricEditor({
  metricKey,
  metric,
  thresholdUnit,
  thresholdMax,
  thresholdStep,
  direction,
  criticalUnit,
  criticalMin,
  criticalMax,
  criticalStep,
  errors,
  update,
}: {
  metricKey: 'average_rating' | 'response_rate' | 'response_time';
  metric: MetricDraft;
  thresholdUnit: string;
  thresholdMax: number;
  thresholdStep: number;
  direction: 'ascending' | 'descending';
  criticalUnit: string;
  criticalMin: number;
  criticalMax: number;
  criticalStep: number;
  errors: Record<string, string>;
  update: DraftUpdater;
}) {
  return (
    <SpaceBetween size="l">
      <CurveTable
        items={metric.thresholds}
        path={`${metricKey}.thresholds`}
        thresholdUnit={thresholdUnit}
        thresholdMax={thresholdMax}
        thresholdStep={thresholdStep}
        direction={direction}
        errors={errors}
        updateItem={(index, key, value) =>
          update((draft) => {
            draft[metricKey].thresholds[index][key] = value;
          })
        }
        addItem={(index, item) =>
          update((draft) => {
            draft[metricKey].thresholds.splice(index, 0, item);
          })
        }
        removeItem={(index) =>
          update((draft) => {
            draft[metricKey].thresholds.splice(index, 1);
          })
        }
      />
      <Container header={<Header variant="h3">Status cutoffs</Header>}>
        <StatusFields
          status={metric.status}
          path={`${metricKey}.status`}
          errors={errors}
          update={update}
        />
      </Container>
      <Container header={<Header variant="h3">Critical cutoff</Header>}>
        <SliderField
          label="Critical cutoff"
          unit={criticalUnit}
          value={metric.critical_threshold}
          min={criticalMin}
          max={criticalMax}
          step={criticalStep}
          error={errors[`${metricKey}.critical_threshold`]}
          onChange={(value) =>
            update((draft) => {
              draft[metricKey].critical_threshold = value;
            })
          }
        />
      </Container>
    </SpaceBetween>
  );
}

export default function ScorecardThresholdsPage() {
  const queryClient = useQueryClient();
  const compact = useMedia('(max-width: 767px)');
  const query = useQuery({
    queryKey: ['scorecard-thresholds'],
    queryFn: getScorecardThresholds,
    staleTime: 30_000,
  });
  const [draft, setDraft] = useState<ThresholdsDraft>();
  const [savedDraft, setSavedDraft] = useState('');
  const [selectedSource, setSelectedSource] = useState('google');
  const [selectedBusinessType, setSelectedBusinessType] = useState('Other');
  const [showSuccess, setShowSuccess] = useState(false);

  const draftSignature = draft ? JSON.stringify(draft) : '';
  const dirty = Boolean(draft && savedDraft && draftSignature !== savedDraft);
  const validation = draft ? validateThresholdsDraft(draft) : { errors: {} };
  const errorCount = Object.keys(validation.errors).length;

  useEffect(() => {
    if (!query.data || dirty) return;
    const next = createThresholdsDraft(query.data);
    setDraft(next);
    setSavedDraft(JSON.stringify(next));
  }, [dirty, query.data]);

  useEffect(() => {
    if (!dirty) return;
    const preventUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', preventUnload);
    return () => window.removeEventListener('beforeunload', preventUnload);
  }, [dirty]);

  const saveMutation = useMutation({
    mutationFn: updateScorecardThresholds,
    onSuccess: (saved) => {
      queryClient.setQueryData(['scorecard-thresholds'], saved);
      const next = createThresholdsDraft(saved);
      setDraft(next);
      setSavedDraft(JSON.stringify(next));
      setShowSuccess(true);
    },
  });

  const update: DraftUpdater = (mutator) => {
    setShowSuccess(false);
    setDraft((current) => {
      if (!current) return current;
      const next = structuredClone(current);
      mutator(next);
      return next;
    });
  };

  const discard = () => {
    if (!query.data) return;
    const next = createThresholdsDraft(query.data);
    setDraft(next);
    setSavedDraft(JSON.stringify(next));
    setShowSuccess(false);
  };

  const save = () => {
    if (validation.update) saveMutation.mutate(validation.update);
  };

  const review = draft?.review_volume;
  const sources = review ? Object.keys(review.thresholds) : [];
  const businessTypes = review ? Object.keys(review.customer_volume_defaults) : [];
  const selectedCurve = review?.thresholds[selectedSource]?.[selectedBusinessType];
  const selectedStatus = review?.statuses[selectedSource]?.[selectedBusinessType];
  const selectedCritical = review?.critical_thresholds[selectedSource]?.[selectedBusinessType];
  const reviewPath = `review_volume.${selectedSource}.${selectedBusinessType}`;
  const gradeEntries = draft ? orderedGradeEntries(draft.grade_thresholds) : [];
  const weightEntries = draft ? Object.entries(draft.weights) : [];

  const customerTermField = (businessType: string) => (
    <FormField
      label={`${businessType} customer term`}
      errorText={validation.errors[`review_volume.customer_terms.${businessType}`]}
    >
      <Input
        value={review?.customer_terms[businessType] ?? ''}
        onChange={({ detail }) =>
          update((next) => {
            next.review_volume.customer_terms[businessType] = detail.value;
          })
        }
      />
    </FormField>
  );

  return (
    <DashboardViewLayout
      maxContentWidth={1180}
      breadcrumbs={[
        { text: 'Scorecards', href: '/dashboard/scorecard' },
        { text: 'Thresholds', href: '/dashboard/scorecard/thresholds' },
      ]}
      header={
        <Header variant="h1" description="Active scoring rules for newly created scorecards.">
          Scorecard thresholds
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Alert type="info">
          Saving changes affects new scorecards only. Existing scorecards keep the rules they were
          created with.
        </Alert>
        {query.isPending ? (
          <Container>
            <StatusIndicator type="loading">
              <Spinner /> Loading thresholds
            </StatusIndicator>
          </Container>
        ) : null}
        {query.isError ? (
          <Alert
            type="error"
            header="Scorecard thresholds could not be loaded"
            action={<Button onClick={() => query.refetch()}>Retry</Button>}
          >
            {query.error.message}
          </Alert>
        ) : null}
        {draft && review ? (
          <>
            <Container>
              <SpaceBetween size="s">
                <StatusIndicator
                  type={dirty ? (errorCount ? 'warning' : 'in-progress') : 'success'}
                >
                  {dirty
                    ? errorCount
                      ? `${errorCount} field ${errorCount === 1 ? 'needs' : 'fields need'} attention`
                      : 'Unsaved changes'
                    : 'Active configuration'}
                </StatusIndicator>
                {saveMutation.isError ? (
                  <Alert type="error" header="Changes were not saved">
                    {saveMutation.error.message}
                  </Alert>
                ) : null}
                {showSuccess && !dirty ? (
                  <Alert type="success">Thresholds saved for new scorecards.</Alert>
                ) : null}
                <SpaceBetween direction="horizontal" size="s">
                  <Button disabled={!dirty || saveMutation.isPending} onClick={discard}>
                    Discard changes
                  </Button>
                  <Button
                    variant="primary"
                    loading={saveMutation.isPending}
                    disabled={!dirty || errorCount > 0}
                    onClick={save}
                  >
                    Save thresholds
                  </Button>
                </SpaceBetween>
              </SpaceBetween>
            </Container>
            <ThresholdCalculator config={validation.update} />
            <Tabs
              tabs={[
                {
                  id: 'volume',
                  label: 'Monthly customers',
                  content: (
                    <Container>
                      <Table
                        variant="embedded"
                        wrapLines
                        header={
                          <Header
                            variant="h2"
                            description="Defaults used when a new scorecard does not provide monthly customers."
                          >
                            Default monthly customers
                          </Header>
                        }
                        items={businessTypes}
                        columnDefinitions={
                          compact
                            ? [
                                {
                                  id: 'volume-mobile',
                                  header: 'Business type',
                                  cell: (businessType) => (
                                    <SpaceBetween size="s">
                                      {customerTermField(businessType)}
                                      <CustomerVolumeField
                                        label={`${businessType} default`}
                                        value={review.customer_volume_defaults[businessType]}
                                        error={
                                          validation.errors[
                                            `review_volume.customer_volume_defaults.${businessType}`
                                          ]
                                        }
                                        onChange={(value) =>
                                          update((next) => {
                                            next.review_volume.customer_volume_defaults[
                                              businessType
                                            ] = value;
                                          })
                                        }
                                      />
                                    </SpaceBetween>
                                  ),
                                },
                              ]
                            : [
                                {
                                  id: 'business-type',
                                  header: 'Business type',
                                  cell: (businessType) => (
                                    <Box fontWeight="bold">{businessType}</Box>
                                  ),
                                },
                                {
                                  id: 'customers',
                                  header: 'Default customers per month',
                                  cell: (businessType) => (
                                    <CustomerVolumeField
                                      label={`${businessType} default monthly customers`}
                                      value={review.customer_volume_defaults[businessType]}
                                      error={
                                        validation.errors[
                                          `review_volume.customer_volume_defaults.${businessType}`
                                        ]
                                      }
                                      onChange={(value) =>
                                        update((next) => {
                                          next.review_volume.customer_volume_defaults[
                                            businessType
                                          ] = value;
                                        })
                                      }
                                    />
                                  ),
                                },
                                {
                                  id: 'customer-term',
                                  header: 'Customer term (plural)',
                                  cell: customerTermField,
                                },
                              ]
                        }
                      />
                    </Container>
                  ),
                },
                {
                  id: 'review-volume',
                  label: 'Review volume',
                  content: (
                    <SpaceBetween size="l">
                      <Container header={<Header variant="h2">Review-volume allocation</Header>}>
                        <SpaceBetween size="l">
                          <ColumnLayout columns={2}>
                            <FormField label="Review source">
                              <Select
                                selectedOption={{ label: selectedSource, value: selectedSource }}
                                options={sources.map((source) => ({
                                  label: source,
                                  value: source,
                                }))}
                                onChange={({ detail }) =>
                                  setSelectedSource(detail.selectedOption.value ?? sources[0])
                                }
                              />
                            </FormField>
                            <FormField label="Business type">
                              <Select
                                selectedOption={{
                                  label: selectedBusinessType,
                                  value: selectedBusinessType,
                                }}
                                options={businessTypes.map((businessType) => ({
                                  label: businessType,
                                  value: businessType,
                                }))}
                                onChange={({ detail }) =>
                                  setSelectedBusinessType(
                                    detail.selectedOption.value ?? businessTypes[0],
                                  )
                                }
                              />
                            </FormField>
                          </ColumnLayout>
                          {selectedCurve ? (
                            <CurveTable
                              items={selectedCurve}
                              path={`${reviewPath}.thresholds`}
                              thresholdUnit="% of customers"
                              thresholdMax={100}
                              thresholdStep={0.01}
                              direction="descending"
                              errors={validation.errors}
                              updateItem={(index, key, value) =>
                                update((next) => {
                                  next.review_volume.thresholds[selectedSource][
                                    selectedBusinessType
                                  ][index][key] = value;
                                })
                              }
                              addItem={(index, item) =>
                                update((next) => {
                                  next.review_volume.thresholds[selectedSource][
                                    selectedBusinessType
                                  ].splice(index, 0, item);
                                })
                              }
                              removeItem={(index) =>
                                update((next) => {
                                  next.review_volume.thresholds[selectedSource][
                                    selectedBusinessType
                                  ].splice(index, 1);
                                })
                              }
                            />
                          ) : null}
                        </SpaceBetween>
                      </Container>
                      {selectedStatus && selectedCritical !== undefined ? (
                        <Container
                          header={<Header variant="h2">Status and critical cutoffs</Header>}
                        >
                          <SpaceBetween size="l">
                            <StatusFields
                              status={selectedStatus}
                              path={`${reviewPath}.status`}
                              errors={validation.errors}
                              update={update}
                            />
                            <SliderField
                              label="Critical cutoff"
                              unit="% allocation"
                              value={selectedCritical}
                              min={0}
                              max={100}
                              step={1}
                              error={validation.errors[`${reviewPath}.critical`]}
                              onChange={(value) =>
                                update((next) => {
                                  next.review_volume.critical_thresholds[selectedSource][
                                    selectedBusinessType
                                  ] = value;
                                })
                              }
                            />
                          </SpaceBetween>
                        </Container>
                      ) : null}
                      <ExpandableSection headerText="Fallback mapping" variant="container">
                        <SpaceBetween size="l">
                          <ColumnLayout columns={2}>
                            <FormField label="Unknown review source">
                              <Select
                                selectedOption={{
                                  label: review.unknown_source_default,
                                  value: review.unknown_source_default,
                                }}
                                options={sources.map((source) => ({
                                  label: source,
                                  value: source,
                                }))}
                                onChange={({ detail }) =>
                                  update((next) => {
                                    next.review_volume.unknown_source_default =
                                      detail.selectedOption.value ?? sources[0];
                                  })
                                }
                              />
                            </FormField>
                            <FormField label="Unknown business type">
                              <Select
                                selectedOption={{
                                  label: review.unknown_business_type_default,
                                  value: review.unknown_business_type_default,
                                }}
                                options={businessTypes.map((businessType) => ({
                                  label: businessType,
                                  value: businessType,
                                }))}
                                onChange={({ detail }) =>
                                  update((next) => {
                                    next.review_volume.unknown_business_type_default =
                                      detail.selectedOption.value ?? businessTypes[0];
                                  })
                                }
                              />
                            </FormField>
                          </ColumnLayout>
                          <Table
                            variant="embedded"
                            items={Object.keys(review.source_map)}
                            columnDefinitions={[
                              {
                                id: 'source',
                                header: 'Incoming source',
                                cell: (source) => source,
                              },
                              {
                                id: 'mapped',
                                header: 'Use allocation curve',
                                cell: (source) => (
                                  <Select
                                    selectedOption={{
                                      label: review.source_map[source],
                                      value: review.source_map[source],
                                    }}
                                    options={sources.map((value) => ({ label: value, value }))}
                                    onChange={({ detail }) =>
                                      update((next) => {
                                        next.review_volume.source_map[source] =
                                          detail.selectedOption.value ?? sources[0];
                                      })
                                    }
                                  />
                                ),
                              },
                            ]}
                          />
                        </SpaceBetween>
                      </ExpandableSection>
                    </SpaceBetween>
                  ),
                },
                ...METRICS.map((metric) => ({
                  id: metric.key,
                  label: metric.label,
                  content: (
                    <MetricEditor
                      metricKey={metric.key}
                      metric={draft[metric.key]}
                      thresholdUnit={metric.thresholdUnit}
                      thresholdMax={metric.thresholdMax}
                      thresholdStep={metric.thresholdStep}
                      direction={metric.direction}
                      criticalUnit={metric.criticalUnit}
                      criticalMin={metric.criticalMin}
                      criticalMax={metric.criticalMax}
                      criticalStep={metric.criticalStep}
                      errors={validation.errors}
                      update={update}
                    />
                  ),
                })),
                {
                  id: 'score',
                  label: 'Grades and weights',
                  content: (
                    <ColumnLayout columns={2}>
                      <Container header={<Header variant="h2">Grade bands</Header>}>
                        <SpaceBetween size="s">
                          {gradeEntries.map(([grade, value], index) => (
                            <SliderField
                              key={grade}
                              label={grade}
                              unit="% minimum score"
                              value={value}
                              min={
                                index === gradeEntries.length - 1
                                  ? 0
                                  : Number(gradeEntries[index + 1][1]) + 1
                              }
                              max={index === 0 ? 100 : Number(gradeEntries[index - 1][1]) - 1}
                              step={1}
                              error={validation.errors[`grade_thresholds.${grade}`]}
                              onChange={(nextValue) =>
                                update((next) => {
                                  next.grade_thresholds[grade] = nextValue;
                                })
                              }
                            />
                          ))}
                        </SpaceBetween>
                      </Container>
                      <Container header={<Header variant="h2">Score weights</Header>}>
                        <SpaceBetween size="s">
                          <StatusIndicator type={validation.errors.weights ? 'error' : 'success'}>
                            {validation.errors.weights
                              ? validation.errors.weights
                              : 'Weights are linked and total 100%'}
                          </StatusIndicator>
                          {weightEntries.map(([key, value]) => (
                            <SliderField
                              key={key}
                              label={key.replaceAll('_', ' ')}
                              unit="% weight"
                              value={value}
                              min={0}
                              max={100}
                              step={0.1}
                              error={validation.errors[`weights.${key}`]}
                              onChange={(nextValue) =>
                                update((next) => {
                                  rebalanceWeights(next.weights, key, nextValue);
                                })
                              }
                            />
                          ))}
                        </SpaceBetween>
                      </Container>
                    </ColumnLayout>
                  ),
                },
              ]}
            />
          </>
        ) : null}
      </SpaceBetween>
    </DashboardViewLayout>
  );
}
